import { useRef, useState } from 'react';
import { Formik } from 'formik';

import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
} from '@src/design-system/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@src/design-system/tabs';
import { Button } from '@src/design-system/button';

import {
  createHostFormInitialValues,
  hostFormSchema,
  importHostsFormSchema,
  type THostFormValues,
  type TImportHostFormValues,
} from '@shared/forms/host';

import type { TCountry } from '@shared/types/country';
import type { TPlacementHostCompany } from '@shared/types/placement-host-company';
import placementHostCompanyApi, {
  importHosts,
} from '@features/placements/services/placement-host-company-api';
import { CreateHostForm } from './components/create-host-form';
import ImportHostsForm from './components/import-hosts-form';
import { useDialogContext } from '@src/design-system/dialog/use-dialog-context';

type THostFormValuesUnion = THostFormValues | TImportHostFormValues;

export function CreateHostModal({
  host,
  isTableView,
}: {
  host?: TPlacementHostCompany;
  isTableView?: boolean;
}) {
  const [currentTab, setCurrentTab] = useState<'single' | 'import'>('single');
  const closeRef = useRef<HTMLButtonElement>(null);

  const { mutate: createHost, isPending: isCreating } =
    placementHostCompanyApi.useCreate();
  const { mutate: editHost, isPending: isEditing } =
    placementHostCompanyApi.useUpdate();
  const { mutate: importHostsFromFile, isPending: isImporting } =
    importHosts.useCreate();

  const handleSubmit = (values: THostFormValuesUnion) => {
    const onSuccess = () => {
      closeRef.current?.click();
    };

    if (currentTab === 'import') {
      importHostsFromFile(
        { file: (values as TImportHostFormValues).file as File },
        {
          onSuccess,
        }
      );
      return;
    }

    const singleValues = values as THostFormValues;
    const isFieldChanged = (fieldName: keyof THostFormValues) =>
      singleValues[fieldName] !==
      (createHostFormInitialValues(host) as THostFormValues)[fieldName];

    const data = {
      name: singleValues.companyName,
      description: singleValues.companyDescription,
      address_line_1: singleValues.address1,
      address_line_2: singleValues.address2,
      country: singleValues.country as unknown as TCountry,
      city: singleValues.city,
      state: singleValues.state,
      postal_code: singleValues.postalCode,
      ...(isFieldChanged('companyLogo') && {
        logo: singleValues.companyLogo as string | undefined,
      }),
      ...(isFieldChanged('contactName') && {
        contact_name: singleValues.contactName,
      }),
      ...(isFieldChanged('contactJobTitle') && {
        contact_job_title: singleValues.contactJobTitle,
      }),
      ...(isFieldChanged('contactEmail') && {
        contact_email: singleValues.contactEmail,
      }),
      ...(isFieldChanged('contactPhoneNumber') && {
        contact_phone_number: singleValues.contactPhoneNumber,
      }),
      ...(isFieldChanged('website') && { website: singleValues.website }),
    };

    if (host) {
      editHost(
        { id: host.id, ...data },
        {
          onSuccess,
        }
      );
    } else {
      createHost(data, {
        onSuccess,
      });
    }
  };

  const { formikRef } = useDialogContext();
  const isPending = isCreating || isEditing || isImporting;

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={createHostFormInitialValues(host, currentTab)}
      validationSchema={
        currentTab === 'import' ? importHostsFormSchema : hostFormSchema
      }
      key={currentTab}
      innerRef={formikRef}
    >
      {({ setFieldValue, values, submitForm }) => {
        return (
          <>
            <DialogHeader>
              {host ? 'Edit Host Company' : 'Create Host Company'}
            </DialogHeader>
            <DialogBody className='space-y-6'>
              {host ? (
                <CreateHostForm
                  values={values as THostFormValues}
                  setFieldValue={setFieldValue}
                  isTableView={isTableView}
                />
              ) : (
                <Tabs
                  value={currentTab}
                  onValueChange={(value) =>
                    setCurrentTab(value as 'single' | 'import')
                  }
                >
                  <TabsList className='w-full mb-4'>
                    <TabsTrigger value='single'>Single Host</TabsTrigger>
                    <TabsTrigger value='import'>Import Hosts</TabsTrigger>
                  </TabsList>
                  <TabsContent value='single'>
                    <CreateHostForm
                      values={values as THostFormValues}
                      setFieldValue={setFieldValue}
                      isTableView={isTableView}
                    />
                  </TabsContent>
                  <TabsContent value='import'>
                    <ImportHostsForm
                      values={values as TImportHostFormValues}
                      setFieldValue={setFieldValue}
                    />
                  </TabsContent>
                </Tabs>
              )}
            </DialogBody>
            <DialogFooter className='mt-auto'>
              <Button
                isLoading={isPending}
                className='flex-1'
                type='submit'
                onClick={() => {
                  submitForm();
                }}
              >
                Confirm
              </Button>
              <DialogClose asChild>
                <Button
                  className='w-3/6'
                  variant='primaryOutline'
                  ref={closeRef}
                >
                  Cancel
                </Button>
              </DialogClose>
            </DialogFooter>
          </>
        );
      }}
    </Formik>
  );
}
